import React, { useState, useEffect, useRef } from 'react';
import { api } from '../utils/apiClient';
import { secureStorage } from '../utils/securityUtils';
import { Search, Plus, FileEdit, Trash2, Loader2, FileSpreadsheet, Eye, X, Download, FileText, Upload, AlertTriangle } from 'lucide-react';
import EditCaseModal from './EditCaseModal';
import NuevoCasoModal from './NuevoCasoModal';
import StatusBadge from './ui/StatusBadge';
import EmptyState from './ui/EmptyState';
import TableSkeleton from './ui/TableSkeleton';
import * as XLSX from 'xlsx';
import {
  OPCIONES_CIERRE_MASTER,
  OPCIONES_TIPO_SERVICIO,
  OPCIONES_RESPONSABILIDAD,
  OPCIONES_CHOQUE_SIMPLE,
  getStatusColor
} from '../constants/masterData';

// ─── Formateo de una fila de caso (ID→label) — usado por la pagina y el export ───
const formatCaso = (caso) => {
  const traducir = (valor, lista) => {
    if (valor === null || valor === undefined) return '';
    if (typeof valor === 'number') {
      const match = lista.find(item => item.id === valor);
      return match ? match.label : String(valor);
    }
    const textoLimpio = String(valor).trim().toUpperCase();
    const matchByLabel = lista.find(item => item.label.toUpperCase() === textoLimpio);
    if (matchByLabel) return matchByLabel.label;
    const numVal = parseInt(textoLimpio, 10);
    if (!isNaN(numVal)) {
      const matchById = lista.find(item => item.id === numVal);
      if (matchById) return matchById.label;
    }
    return valor;
  };

  const obtenerID = (valor, lista) => {
    if (typeof valor === 'number') return valor;
    if (!valor && valor !== 0) return null;
    const textoLimpio = String(valor).trim().toUpperCase();
    const aseguradora = (caso.aseguradora || '').toUpperCase();
    const esIKE = aseguradora.includes('IKE');
    let match = lista.find(item => item.label.toUpperCase() === textoLimpio && (esIKE ? item.grupo === 'IKE' : item.grupo === 'ASIS'));
    if (!match) match = lista.find(item => item.label.toUpperCase() === textoLimpio);
    if (!match) {
      const numVal = parseInt(textoLimpio, 10);
      if (!isNaN(numVal)) {
        match = lista.find(item => item.id === numVal);
        if (match) return match.id;
      }
      return null;
    }
    return match.id;
  };

  const rawCierre = caso.cierre_servicio_raw || caso.cierre_servicio;
  const rawTipo   = caso.tipo_servicio_raw   || caso.tipo_servicio;
  const rawResp   = caso.responsabilidad;
  const rawChoque = caso.cierre_choque_simple;

  const idCierre = obtenerID(rawCierre, OPCIONES_CIERRE_MASTER);
  const idTipo   = obtenerID(rawTipo, OPCIONES_TIPO_SERVICIO);
  const idResp   = obtenerID(rawResp, OPCIONES_RESPONSABILIDAD);
  const idChoque = obtenerID(rawChoque, OPCIONES_CHOQUE_SIMPLE);

  const backendCierre = caso.cierre_servicio;
  const backendTipo = caso.tipo_servicio;

  const labelCierre = idCierre !== null
    ? (OPCIONES_CIERRE_MASTER.find(o => o.id === idCierre)?.label || backendCierre || rawCierre || '')
    : (backendCierre && backendCierre !== rawCierre ? backendCierre : (rawCierre || ''));
  const labelTipo = idTipo !== null
    ? (OPCIONES_TIPO_SERVICIO.find(o => o.id === idTipo)?.label || backendTipo || rawTipo || '')
    : (backendTipo && backendTipo !== rawTipo ? backendTipo : (rawTipo || ''));

  const aseguradoraRaw = caso.aseguradora || '';
  const aseguradoraNorm = (() => {
    const lower = aseguradoraRaw.toLowerCase().trim();
    if (lower.includes('asis') && lower.includes('prex')) return 'Assisprex';
    return aseguradoraRaw;
  })();

  return {
    ...caso,
    aseguradora: aseguradoraNorm,
    fechaFiltro: caso.fecha ? caso.fecha.split('/').reverse().join('-') : '',
    id_cierre_servicio: idCierre,
    id_tipo_servicio: idTipo,
    id_responsabilidad: idResp,
    id_cierre_choque_simple: idChoque,
    cierre_servicio: labelCierre || 'SIN ASIGNAR',
    tipo_servicio: labelTipo || 'SIN ASIGNAR',
    responsabilidad: traducir(rawResp, OPCIONES_RESPONSABILIDAD),
    cierre_choque_simple: traducir(rawChoque, OPCIONES_CHOQUE_SIMPLE),
    estadoColor: getStatusColor(labelCierre)
  };
};

// Mapea el estado de filtros del UI a query params del backend (omite defaults).
const buildCasoParams = (filters, searchTerm) => {
  const p = {};
  if (filters.aseguradora && !filters.aseguradora.toLowerCase().includes('todas')) p.aseguradora = filters.aseguradora;
  if (filters.status && !filters.status.toLowerCase().includes('todos')) p.estado = filters.status;
  if (filters.abogado && !filters.abogado.toLowerCase().includes('todos')) p.abogado = filters.abogado;
  if (filters.tipoServicio && !filters.tipoServicio.toLowerCase().includes('todos')) p.tipo_servicio = filters.tipoServicio;
  if (filters.fechaDesde) p.fecha_desde = filters.fechaDesde;
  if (filters.fechaHasta) p.fecha_hasta = filters.fechaHasta;
  if (searchTerm && searchTerm.trim()) p.q = searchTerm.trim();
  return p;
};

// ─── Modal de archivos ───────────────────────────────────────────
const FileViewerModal = ({ expediente, onClose }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchFiles = React.useCallback(async () => {
    try {
      const res = await api.get(`/expedientes/${expediente}/files`);
      setFiles(res.archivos || []);
    } catch (err) {
      console.error('Error al obtener archivos:', err);
    } finally {
      setLoading(false);
    }
  }, [expediente]);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  const handleApprove = (fileName) => {
    alert(`Documento ${fileName} marcado como válido.`);
  };

  const handleReject = async (fileName) => {
    if (window.confirm(`¿Estás seguro de rechazar y eliminar el documento ${fileName}?`)) {
      try {
        await api.delete(`/expedientes/${expediente}/files/${fileName}`);
        alert('Documento eliminado.');
        fetchFiles();
      } catch {
        alert('Error al intentar eliminar el archivo.');
      }
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fadeIn">
      <div className="bg-slate-900 border border-slate-700 w-full max-w-md rounded-3xl shadow-2xl shadow-black/50 overflow-hidden animate-scaleIn">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-800 bg-gradient-to-r from-slate-800/60 to-slate-900 flex justify-between items-center">
          <div>
            <h3 className="font-bold text-white text-sm">Documentación</h3>
            <p className="text-[10px] text-blue-400 font-bold uppercase tracking-widest mt-0.5">{expediente}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-700 rounded-xl transition-colors active:scale-95">
            <X size={18} className="text-slate-400" />
          </button>
        </div>

        {/* Cuerpo */}
        <div className="p-5 max-h-[420px] overflow-y-auto custom-scrollbar">
          {loading ? (
            <div className="flex flex-col items-center py-10 gap-3">
              <Loader2 size={24} className="animate-spin text-blue-500" />
              <p className="text-xs text-slate-500 uppercase font-semibold tracking-wider">Cargando archivos...</p>
            </div>
          ) : files.length > 0 ? (
            <div className="space-y-3">
              {files.map((file, idx) => (
                <div key={idx} className="p-3 rounded-2xl border border-slate-700/50 bg-slate-800/40 hover:bg-slate-800/70 transition-colors">
                  <div className="flex items-center gap-2.5 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-900/40 flex items-center justify-center flex-shrink-0">
                      <FileText size={16} className="text-blue-400" />
                    </div>
                    <span className="text-sm font-medium text-slate-200 truncate">{file}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2 border-t border-slate-700/50 pt-2.5">
                    <a
                      href={`https://app.legalpp.com.co/legalplus/expedientes/${expediente}/files/${file}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs font-semibold text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      <Download size={13} /> Ver documento
                    </a>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleReject(file)}
                        className="flex items-center gap-1 px-2.5 py-1 bg-red-900/30 text-red-400 rounded-lg text-[10px] font-bold uppercase hover:bg-red-900/60 hover:text-red-300 transition-all active:scale-95"
                      >
                        <Trash2 size={11} /> Eliminar
                      </button>
                      <button
                        onClick={() => handleApprove(file)}
                        className="flex items-center gap-1 px-2.5 py-1 bg-emerald-900/30 text-emerald-400 rounded-lg text-[10px] font-bold uppercase hover:bg-emerald-900/60 hover:text-emerald-300 transition-all active:scale-95"
                      >
                        <Plus size={11} /> Aprobar
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              title="Sin documentos adjuntos"
              description="No hay archivos pendientes para este expediente"
              className="py-10"
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Tabla de casos ───────────────────────────────────────────────
const CaseTable = ({ cases, onEdit, onDelete, onViewFiles, onUpload, uploadingId, readOnly }) => (
  <div className="h-full flex flex-col">
    {/* Vista móvil */}
    <div className="md:hidden space-y-3 overflow-y-auto flex-1 p-1 custom-scrollbar">
      {cases.map(caso => (
        <div
          key={`${caso.aseguradora}_${caso.expediente}`}
          className="bg-slate-800/80 rounded-2xl p-4 border border-slate-700/50 shadow-lg hover:border-slate-600/50 transition-all duration-200"
        >
          <div className="flex justify-between items-start pb-3 mb-3 border-b border-slate-700/50">
            <div>
              <span className="text-[10px] font-bold uppercase text-slate-500 tracking-wider">Expediente</span>
              <h3 className="font-bold text-base text-blue-300 break-all font-mono">{caso.expediente}</h3>
            </div>
            <div className="flex items-center gap-0.5 flex-shrink-0 ml-2">
              {!readOnly && <button onClick={() => onUpload(caso.expediente)} className="text-emerald-400 p-2 hover:bg-emerald-900/30 rounded-xl transition-colors active:scale-95"><Upload size={16} /></button>}
              <button onClick={() => onViewFiles(caso.expediente)} className="text-amber-400 p-2 hover:bg-amber-900/30 rounded-xl transition-colors active:scale-95"><Eye size={16} /></button>
              {!readOnly && <button onClick={() => onEdit(caso)} className="text-blue-400 p-2 hover:bg-blue-900/30 rounded-xl transition-colors active:scale-95"><FileEdit size={16} /></button>}
              {!readOnly && <button onClick={() => onDelete(caso.aseguradora, caso.expediente)} className="text-red-400 p-2 hover:bg-red-900/30 rounded-xl transition-colors active:scale-95"><Trash2 size={16} /></button>}
            </div>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-500 text-xs">Cliente</span><span className="text-slate-200 font-medium text-right max-w-[60%] truncate">{caso.nombre}</span></div>
            <div className="flex justify-between"><span className="text-slate-500 text-xs">Aseguradora</span><span className="text-slate-200 font-medium text-right">{caso.aseguradora}</span></div>
            <div className="flex justify-between"><span className="text-slate-500 text-xs">Placa</span><span className="text-emerald-400 font-mono font-bold">{caso.placa}</span></div>
            <div className="flex justify-between"><span className="text-slate-500 text-xs">Abogado</span><span className="text-slate-200 font-medium text-right">{caso.abogado || 'Sin asignar'}</span></div>
          </div>
          <div className="mt-3 pt-3 border-t border-slate-700/50">
            <StatusBadge status={caso.cierre_servicio || 'En progreso'} />
          </div>
        </div>
      ))}
    </div>

    {/* Vista escritorio */}
    <div className="hidden md:flex flex-col flex-1 min-h-0 overflow-hidden rounded-3xl border border-slate-700/50 bg-slate-900/90 shadow-xl">
      <div className="overflow-auto flex-1 custom-scrollbar">
        <table className="min-w-max w-full border-collapse">
          <thead>
            <tr className="border-b border-slate-700 bg-slate-800/70 sticky top-0 z-20">
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 sticky left-0 bg-slate-800/90 z-30 shadow-[2px_0_4px_rgba(0,0,0,0.3)] whitespace-nowrap">Expediente</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">Fecha</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">Hora Asig.</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">Hora Lleg.</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">Aseguradora</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">Cliente Corp.</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">Nombre</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">Teléfono</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">Placa</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">Dirección</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">Tipo Servicio</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">Cuenta</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">Asunto</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">Observación</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">Conciliación</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">Responsabilidad</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">Cierre Choque</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">Abogado</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">Estado</th>
              <th className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-wider text-slate-400 sticky right-0 bg-slate-800/90 z-30 shadow-[-2px_0_4px_rgba(0,0,0,0.3)] whitespace-nowrap">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {cases.map((caso) => (
              <tr
                key={`${caso.aseguradora}_${caso.expediente}`}
                className="border-b border-slate-800/60 hover:bg-slate-800/40 transition-colors duration-150 group"
              >
                <td className="px-4 py-3 whitespace-nowrap sticky left-0 bg-slate-900/95 group-hover:bg-slate-800/60 z-10 shadow-[2px_0_4px_rgba(0,0,0,0.2)] transition-colors">
                  <span className="text-xs font-bold font-mono text-blue-300">{caso.expediente}</span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-400">{caso.fecha}</td>
                <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-400 tabular-nums">{caso.hora_asignacion}</td>
                <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-400 tabular-nums">{caso.hora_llegada}</td>
                <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-300">{caso.aseguradora}</td>
                <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-300">{caso.cliente_corporativo}</td>
                <td className="px-4 py-3 text-xs text-slate-300"><div className="w-44 truncate" title={caso.nombre}>{caso.nombre}</div></td>
                <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-300 tabular-nums">{caso.telefono}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className="text-xs font-mono font-bold text-emerald-400">{caso.placa}</span>
                </td>
                <td className="px-4 py-3 text-xs text-slate-400"><div className="w-40 truncate" title={caso.direccion}>{caso.direccion}</div></td>
                <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-300">{caso.tipo_servicio}</td>
                <td className="px-4 py-3 text-xs text-slate-400"><div className="w-32 truncate" title={caso.cuenta}>{caso.cuenta}</div></td>
                <td className="px-4 py-3 text-xs text-slate-400"><div className="w-40 truncate" title={caso.asunto}>{caso.asunto}</div></td>
                <td className="px-4 py-3 text-xs text-slate-400"><div className="w-40 truncate" title={caso.observacion}>{caso.observacion}</div></td>
                <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-300">{caso.conciliacion}</td>
                <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-300">{caso.responsabilidad}</td>
                <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-300">{caso.cierre_choque_simple}</td>
                <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-300">{caso.abogado || 'Sin asignar'}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <StatusBadge status={caso.cierre_servicio || 'En progreso'} />
                </td>
                <td className="px-4 py-3 whitespace-nowrap sticky right-0 bg-slate-900/95 group-hover:bg-slate-800/60 z-10 shadow-[-2px_0_4px_rgba(0,0,0,0.2)] transition-colors">
                  <div className="flex items-center justify-end gap-0.5">
                    {!readOnly && (
                      <button
                        onClick={() => onUpload(caso.expediente)}
                        disabled={uploadingId === caso.expediente}
                        title="Subir documento"
                        className="p-1.5 rounded-lg text-emerald-500 hover:bg-emerald-900/30 hover:text-emerald-300 transition-all duration-150 active:scale-95 disabled:opacity-50"
                      >
                        {uploadingId === caso.expediente
                          ? <Loader2 size={15} className="animate-spin" />
                          : <Upload size={15} />
                        }
                      </button>
                    )}
                    <button onClick={() => onViewFiles(caso.expediente)} title="Ver archivos" className="p-1.5 rounded-lg text-amber-400 hover:bg-amber-900/30 hover:text-amber-300 transition-all duration-150 active:scale-95"><Eye size={15} /></button>
                    {!readOnly && <button onClick={() => onEdit(caso)} title="Editar caso" className="p-1.5 rounded-lg text-blue-400 hover:bg-blue-900/30 hover:text-blue-300 transition-all duration-150 active:scale-95"><FileEdit size={15} /></button>}
                    {!readOnly && <button onClick={() => onDelete(caso.aseguradora, caso.expediente)} title="Eliminar caso" className="p-1.5 rounded-lg text-red-400 hover:bg-red-900/30 hover:text-red-300 transition-all duration-150 active:scale-95"><Trash2 size={15} /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

// ─── Componente principal ──────────────────────────────────────────
const Casos = ({ user, readOnly = false }) => {
  const [cases, setCases] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentCase, setCurrentCase] = useState(null);
  const [viewingExpediente, setViewingExpediente] = useState(null);
  const [isNuevoCasoOpen, setIsNuevoCasoOpen] = useState(false);

  const fileInputRef = useRef(null);
  const [selectedExpediente, setSelectedExpediente] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);

  const [filters, setFilters] = useState({
    status: 'Todos los estados',
    abogado: 'Todos los abogados',
    aseguradora: 'Todas las aseguradoras',
    tipoServicio: 'Todos los servicios',
    fechaDesde: '',
    fechaHasta: ''
  });

  const [filterOptions, setFilterOptions] = useState({
    status: ['Todos los estados', 'En progreso'],
    abogado: ['Todos los abogados'],
    aseguradora: ['Todas las aseguradoras'],
    tipoServicio: ['Todos los servicios']
  });

  const currentUser = user || secureStorage.getItem('userData');
  const isAdminUser = currentUser?.rol === 'Administrador';

  const handleUploadClick = (expediente) => {
    setSelectedExpediente(expediente);
    fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !selectedExpediente) return;
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    setUploadingId(selectedExpediente);
    try {
      // N9: leer data.rechazados que el backend (#3 Daniel) ya envia.
      const data = await api.post(
        `/expedientes/${selectedExpediente}/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      // N9-bis: el backend (expedientes.py:53) devuelve 'archivos', no 'subidos'.
      const rechazados = (data && Array.isArray(data.rechazados)) ? data.rechazados : [];
      const guardados = (data && Array.isArray(data.archivos)) ? data.archivos : null;
      const subidos = guardados ? guardados.length : Math.max(files.length - rechazados.length, 0);
      if (rechazados.length === 0) {
        alert(`Se subio correctamente: ${subidos} archivo(s).`);
      } else {
        const lineas = rechazados.map((r) => `  - ${r.nombre || r.archivo || r.filename || 'archivo'}: ${r.motivo || r.reason || 'rechazado'}`).join('\n');
        alert(
          `Subidos: ${subidos}\nRechazados: ${rechazados.length}\n\nDetalle de rechazados:\n${lineas}`
        );
      }
      fetchCases();
    } catch (err) {
      const msg = (err && err.response && err.response.data && err.response.data.error) || 'Error al subir el documento';
      alert(msg);
    } finally {
      setUploadingId(null);
      setSelectedExpediente(null);
      e.target.value = null;
    }
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [totalCases, setTotalCases] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const ITEMS_PER_PAGE = 50;

  // F-Perf.2: paginacion + filtrado server-side. La pagina se pide al backend.
  const fetchCases = async (page = currentPage, search = debouncedSearch) => {
    try {
      setLoading(true);
      setError(null);
      const params = { page, per_page: ITEMS_PER_PAGE, ...buildCasoParams(filters, search) };
      const resp = await api.get('/casos', { params });
      const rows = Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []);
      setCases(rows.map(formatCaso));
      setTotalCases(typeof resp?.total === 'number' ? resp.total : rows.length);
    } catch {
      setError('No se pudieron cargar los casos.');
    } finally {
      setLoading(false);
    }
  };

  // Opciones de filtro desde TODO el set (no por pagina) — una sola vez.
  useEffect(() => {
    (async () => {
      try {
        const op = await api.get('/casos/opciones');
        setFilterOptions({
          status: ['Todos los estados', ...(op.estados || [])],
          abogado: ['Todos los abogados', ...(op.abogados || [])],
          aseguradora: ['Todas las aseguradoras', ...(op.aseguradoras || [])],
          tipoServicio: ['Todos los servicios', ...(op.servicios || [])],
        });
      } catch { /* dropdowns quedan con defaults */ }
    })();
  }, []);

  // Debounce de la busqueda: 400ms tras dejar de escribir, y vuelve a pagina 1.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Refetch al cambiar pagina, filtros o busqueda (ya debounced).
  useEffect(() => {
    fetchCases(currentPage, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filters, debouncedSearch]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const handleDeleteCase = async (aseguradora, expediente) => {
    if (window.confirm('¿Estás seguro de eliminar este caso?')) {
      try {
        const apiAseguradora = encodeURIComponent(aseguradora);
        await api.delete(`/casos/${apiAseguradora}/${expediente}`);
        await fetchCases();
      } catch {
        setError('Error al eliminar el caso.');
      }
    }
  };

  const handleEditCase = (caseToEdit) => { setCurrentCase(caseToEdit); setIsModalOpen(true); };
  const handleCloseModal = () => { setIsModalOpen(false); setCurrentCase(null); };

  const handleSaveCase = async (updatedCase) => {
    try {
      const apiAseguradora = encodeURIComponent(updatedCase.aseguradora);
      const expedienteOriginal = updatedCase.expediente || currentCase?.expediente;
      if (!expedienteOriginal) throw new Error('Expediente no definido.');

      let payload = { ...updatedCase };
      if (!isAdminUser) delete payload.expediente;

      await api.put(`/casos/${apiAseguradora}/${expedienteOriginal}`, payload);
      await fetchCases();
      handleCloseModal();
    } catch (err) {
      const msg = err?.message || 'Error al actualizar el caso.';
      setError(msg);
      throw err;
    }
  };

  // F-Perf.2: el backend ya filtra y pagina. `cases` es la pagina actual.
  const totalPages = Math.max(1, Math.ceil(totalCases / ITEMS_PER_PAGE));
  const paginatedCases = cases;
  const hayFiltrosActivos = !!debouncedSearch || Object.values(filters).some(v => v && !v.toLowerCase().includes('todos'));

  const handleExportExcel = async () => {
    try {
      // Exportar TODO el set filtrado (no solo la pagina visible): fetch sin page.
      const resp = await api.get('/casos', { params: buildCasoParams(filters, debouncedSearch) });
      const all = (Array.isArray(resp) ? resp : (resp?.data || [])).map(formatCaso);
      const dataToExport = all.map(caso => ({
      expediente: caso.expediente,
      fecha: caso.fecha,
      hora_asignacion: caso.hora_asignacion,
      hora_llegada: caso.hora_llegada,
      aseguradora: caso.aseguradora,
      cliente_corporativo: caso.cliente_corporativo,
      nombre: caso.nombre,
      telefono: caso.telefono,
      placa: caso.placa,
      direccion: caso.direccion,
      tipo_servicio: caso.tipo_servicio,
      cuenta: caso.cuenta,
      asunto: caso.asunto,
      observacion: caso.observacion,
      conciliacion: caso.conciliacion,
      responsabilidad: caso.responsabilidad,
      cierre_choque_simple: caso.cierre_choque_simple,
      abogado: caso.abogado,
      cierre_servicio: caso.cierre_servicio
    }));
      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Casos');
      XLSX.writeFile(workbook, `Reporte_LegalPlus_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch {
      setError('No se pudo exportar el Excel.');
    }
  };

  // Clase reutilizable para selects de filtro
  const selectClass = 'w-full rounded-xl bg-slate-800 border border-slate-700 text-slate-100 px-3 py-2 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/70 transition-all duration-200';
  const labelClass = 'block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5';

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-slate-950 to-slate-900 text-slate-100 p-4 lg:p-5 gap-4 overflow-hidden">

      {/* Input oculto para archivos */}
      <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileChange} />

      {/* ── Header de sección ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 flex-none">
        <div>
          <h1 className="text-2xl font-extrabold text-white tracking-tight">Gestión de Casos</h1>
          <p className="text-xs text-slate-500 mt-0.5">
            {loading ? 'Cargando...' : `${totalCases} caso${totalCases !== 1 ? 's' : ''} encontrado${totalCases !== 1 ? 's' : ''}`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleExportExcel}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-700 hover:bg-emerald-600 text-white text-sm font-semibold shadow-lg shadow-emerald-900/20 transition-all duration-200 hover:-translate-y-0.5 active:scale-95"
          >
            <FileSpreadsheet size={16} /> Exportar Excel
          </button>
          <button
            onClick={() => setIsNuevoCasoOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold shadow-lg shadow-blue-900/20 transition-all duration-200 hover:-translate-y-0.5 active:scale-95"
          >
            <Plus size={16} /> Nuevo Caso
          </button>
        </div>
      </div>

      {/* ── Panel de filtros ── */}
      <div className="flex-none rounded-2xl bg-slate-800/40 border border-slate-700/40 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 items-end">
          {/* Búsqueda */}
          <div className="relative sm:col-span-2">
            <label className={labelClass}>Búsqueda</label>
            <input
              type="text"
              placeholder="Expediente, nombre, placa..."
              className="w-full pl-8 pr-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-slate-100 placeholder:text-slate-600 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/70 transition-all duration-200"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search size={14} className="absolute left-2.5 bottom-2.5 text-slate-500" />
          </div>

          <div>
            <label className={labelClass}>Estado</label>
            <select name="status" value={filters.status} onChange={handleFilterChange} className={selectClass}>
              {filterOptions.status.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Abogado</label>
            <select name="abogado" value={filters.abogado} onChange={handleFilterChange} className={selectClass}>
              {filterOptions.abogado.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Aseguradora</label>
            <select name="aseguradora" value={filters.aseguradora} onChange={handleFilterChange} className={`${selectClass} uppercase`}>
              {filterOptions.aseguradora.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Servicio</label>
            <select name="tipoServicio" value={filters.tipoServicio} onChange={handleFilterChange} className={`${selectClass} uppercase`}>
              {filterOptions.tipoServicio.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Desde</label>
            <input type="date" name="fechaDesde" value={filters.fechaDesde} onChange={handleFilterChange} className={selectClass} />
          </div>
          <div>
            <label className={labelClass}>Hasta</label>
            <input type="date" name="fechaHasta" value={filters.fechaHasta} onChange={handleFilterChange} className={selectClass} />
          </div>
        </div>
      </div>

      {/* ── Error banner ── */}
      {error && (
        <div className="flex-none bg-red-900/30 border border-red-700/50 text-red-300 text-sm rounded-2xl px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <AlertTriangle size={15} className="text-red-400 flex-shrink-0" />
            <span>{error}</span>
          </div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-200 flex-shrink-0 p-1 rounded-lg hover:bg-red-900/40 transition-colors active:scale-95">
            <X size={15} />
          </button>
        </div>
      )}

      {/* ── Contenido principal ── */}
      <div className="flex-1 min-h-0">
        {loading ? (
          <div className="rounded-3xl overflow-hidden border border-slate-700/50 bg-slate-900/90 shadow-xl h-full">
            <TableSkeleton rows={10} cols={8} />
          </div>
        ) : cases.length === 0 ? (
          <div className="rounded-3xl overflow-hidden border border-slate-700/50 bg-slate-900/90 shadow-xl h-full flex items-center justify-center">
            <EmptyState
              title={hayFiltrosActivos ? 'Sin resultados' : 'No hay casos registrados'}
              description={hayFiltrosActivos ? 'Ningún caso coincide con los filtros aplicados' : 'Los casos aparecerán aquí cuando se registren'}
            />
          </div>
        ) : (
          <div className="flex flex-col h-full min-h-0">
            <div className="flex-1 min-h-0">
              <CaseTable
                cases={paginatedCases}
                onEdit={handleEditCase}
                onDelete={handleDeleteCase}
                onViewFiles={setViewingExpediente}
                onUpload={handleUploadClick}
                uploadingId={uploadingId}
                readOnly={readOnly}
              />
            </div>
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-3 border-t border-slate-700/50 bg-slate-900/80 flex-shrink-0">
                <span className="text-xs text-slate-400 text-center sm:text-left">
                  {((currentPage - 1) * ITEMS_PER_PAGE) + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, totalCases)} de {totalCases} casos
                </span>
                <div className="flex items-center justify-center gap-1">
                  <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="px-2 py-1 rounded-lg text-xs text-slate-400 hover:bg-slate-700 disabled:opacity-30 transition-colors">«</button>
                  <button onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1} className="px-2 py-1 rounded-lg text-xs text-slate-400 hover:bg-slate-700 disabled:opacity-30 transition-colors">‹</button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const start = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
                    const page = start + i;
                    return page <= totalPages ? (
                      <button key={page} onClick={() => setCurrentPage(page)} className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${currentPage === page ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-slate-700'}`}>{page}</button>
                    ) : null;
                  })}
                  <button onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages} className="px-2 py-1 rounded-lg text-xs text-slate-400 hover:bg-slate-700 disabled:opacity-30 transition-colors">›</button>
                  <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="px-2 py-1 rounded-lg text-xs text-slate-400 hover:bg-slate-700 disabled:opacity-30 transition-colors">»</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modales */}
      {isNuevoCasoOpen && (
        <NuevoCasoModal
          onClose={() => setIsNuevoCasoOpen(false)}
          onCreated={fetchCases}
        />
      )}
      {viewingExpediente && (
        <FileViewerModal expediente={viewingExpediente} onClose={() => setViewingExpediente(null)} />
      )}
      <EditCaseModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveCase}
        caseData={currentCase}
        filterOptions={filterOptions}
        canEditExpediente={isAdminUser}
      />
    </div>
  );
};

export default Casos;
